import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { CONTACT, PROJECT_TYPES, SERVICE_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
    name: "",
    phone: "",
    email: "",
    location: "",
    service_required: "",
    project_type: "",
    message: "",
};

function validate(form) {
    const errors = {};
    if (form.name.trim().length < 2) errors.name = "Please enter your full name.";
    if (!/^[0-9+\-\s]{8,15}$/.test(form.phone.trim())) errors.phone = "Please enter a valid phone number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = "Please enter a valid email address.";
    if (form.location.trim().length < 2) errors.location = "Please enter your location / site area.";
    if (!form.service_required) errors.service_required = "Please select a service.";
    if (!form.project_type) errors.project_type = "Please select a project type.";
    if (form.message.trim().length < 10) errors.message = "Please briefly describe your requirement (min 10 characters).";
    return errors;
}

export default function Contact() {
    const [searchParams] = useSearchParams();
    const presetService = searchParams.get("service") || "";
    const [form, setForm] = useState({ ...EMPTY, service_required: presetService });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);

    const set = (key) => (e) => {
        const value = e?.target ? e.target.value : e;
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = validate(form);
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            toast.error("Please correct the highlighted fields.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/enquiries`, form);
            setSuccess(data.enquiry_id);
            setForm({ ...EMPTY });
            toast.success("Enquiry submitted successfully.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again or call us directly.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputClass =
        "rounded-none border-slate-300 bg-white focus-visible:ring-amber focus-visible:ring-1 focus-visible:ring-offset-0";
    const errText = (key) =>
        errors[key] ? (
            <p className="mt-1.5 text-xs text-red-600" data-testid={`form-error-${key}`}>
                {errors[key]}
            </p>
        ) : null;

    return (
        <div>
            <Seo
                title="Contact The Sens Civil Works Consultancy | Gorakhpur"
                description="Request a consultation or site inspection — The Sens Civil Works Consultancy, Gorakhpur, Uttar Pradesh. Call 9415212751 / 9208318881 or send the enquiry form."
            />
            <PageHero
                overline="Contact"
                title="Request a Consultation"
                description="Call, WhatsApp or send the form — describe your project and the decision you are facing, and you will be guided on the next step."
            />

            <section className="bg-white py-20 lg:py-28" data-testid="contact-section">
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-12 lg:px-12">
                    {/* Contact details */}
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">Reach Us Directly</p>
                            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy">
                                {CONTACT.business}
                            </h2>
                            <ul className="mt-8 space-y-6">
                                <li className="flex items-start gap-4">
                                    <MapPin size={20} className="mt-0.5 shrink-0 text-amber" />
                                    <div>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Location</p>
                                        <p className="mt-1 text-sm text-navy" data-testid="contact-location">
                                            {CONTACT.person} · {CONTACT.location}
                                        </p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <Phone size={20} className="mt-0.5 shrink-0 text-amber" />
                                    <div>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Phone</p>
                                        <p className="mt-1 text-sm text-navy">
                                            <a href={`tel:+91${CONTACT.phone1}`} className="hover:text-amber" data-testid="contact-phone-1">
                                                +91 {CONTACT.phone1}
                                            </a>
                                            {" · "}
                                            <a href={`tel:+91${CONTACT.phone2}`} className="hover:text-amber" data-testid="contact-phone-2">
                                                +91 {CONTACT.phone2}
                                            </a>
                                        </p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <Mail size={20} className="mt-0.5 shrink-0 text-amber" />
                                    <div>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email</p>
                                        <a
                                            href={`mailto:${CONTACT.email}`}
                                            className="mt-1 block text-sm text-navy hover:text-amber"
                                            data-testid="contact-email"
                                        >
                                            {CONTACT.email}
                                        </a>
                                    </div>
                                </li>
                            </ul>
                            <a
                                href={CONTACT.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-10 inline-flex items-center gap-2 bg-[#25D366] px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[#1EBE59]"
                                data-testid="contact-whatsapp-btn"
                            >
                                <MessageCircle size={16} /> Chat on WhatsApp
                            </a>
                            <p className="mt-6 max-w-sm text-xs leading-relaxed text-slate-500">
                                Prefer to talk first? A short call is usually enough to understand your requirement and
                                agree the right scope.
                            </p>
                        </Reveal>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-7">
                        <Reveal delay={0.1}>
                            {success ? (
                                <div
                                    className="border border-slate-200 bg-background p-10 text-center"
                                    data-testid="contact-success-message"
                                >
                                    <CheckCircle2 size={44} className="mx-auto text-amber" />
                                    <h2 className="mt-6 font-display text-2xl font-bold text-navy">
                                        Enquiry Received
                                    </h2>
                                    <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-600">
                                        Thank you. Your enquiry has been recorded with reference{" "}
                                        <span className="font-mono font-semibold text-navy">{success}</span>. You will
                                        be contacted shortly on the phone number or email provided.
                                    </p>
                                    <button
                                        onClick={() => setSuccess(null)}
                                        className="mt-8 border border-navy px-6 py-3 font-mono text-xs uppercase tracking-[0.18em] text-navy transition-colors duration-300 hover:bg-navy hover:text-white"
                                        data-testid="contact-send-another-btn"
                                    >
                                        Send Another Enquiry
                                    </button>
                                </div>
                            ) : (
                                <form
                                    onSubmit={onSubmit}
                                    className="border border-slate-200 bg-background p-8 lg:p-10"
                                    data-testid="contact-form"
                                    noValidate
                                >
                                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                        <div>
                                            <Label htmlFor="name" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                                                Name *
                                            </Label>
                                            <Input
                                                id="name"
                                                value={form.name}
                                                onChange={set("name")}
                                                className={`mt-2 ${inputClass}`}
                                                placeholder="Your full name"
                                                data-testid="form-name-input"
                                            />
                                            {errText("name")}
                                        </div>
                                        <div>
                                            <Label htmlFor="phone" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                                                Phone *
                                            </Label>
                                            <Input
                                                id="phone"
                                                value={form.phone}
                                                onChange={set("phone")}
                                                className={`mt-2 ${inputClass}`}
                                                placeholder="e.g. 94152 12751"
                                                data-testid="form-phone-input"
                                            />
                                            {errText("phone")}
                                        </div>
                                        <div>
                                            <Label htmlFor="email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                                                Email *
                                            </Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                value={form.email}
                                                onChange={set("email")}
                                                className={`mt-2 ${inputClass}`}
                                                placeholder="you@example.com"
                                                data-testid="form-email-input"
                                            />
                                            {errText("email")}
                                        </div>
                                        <div>
                                            <Label htmlFor="location" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                                                Location *
                                            </Label>
                                            <Input
                                                id="location"
                                                value={form.location}
                                                onChange={set("location")}
                                                className={`mt-2 ${inputClass}`}
                                                placeholder="Site / project location"
                                                data-testid="form-location-input"
                                            />
                                            {errText("location")}
                                        </div>
                                        <div>
                                            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                                                Service Required *
                                            </Label>
                                            <Select value={form.service_required} onValueChange={set("service_required")}>
                                                <SelectTrigger
                                                    className={`mt-2 ${inputClass}`}
                                                    data-testid="form-service-select"
                                                >
                                                    <SelectValue placeholder="Select a service" />
                                                </SelectTrigger>
                                                <SelectContent className="rounded-none">
                                                    {SERVICE_OPTIONS.map((s) => (
                                                        <SelectItem key={s} value={s} data-testid={`form-service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                                            {s}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {errText("service_required")}
                                        </div>
                                        <div>
                                            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                                                Project Type *
                                            </Label>
                                            <Select value={form.project_type} onValueChange={set("project_type")}>
                                                <SelectTrigger
                                                    className={`mt-2 ${inputClass}`}
                                                    data-testid="form-project-type-select"
                                                >
                                                    <SelectValue placeholder="Select project type" />
                                                </SelectTrigger>
                                                <SelectContent className="rounded-none">
                                                    {PROJECT_TYPES.map((p) => (
                                                        <SelectItem key={p} value={p} data-testid={`form-project-option-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                                            {p}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {errText("project_type")}
                                        </div>
                                        <div className="sm:col-span-2">
                                            <Label htmlFor="message" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                                                Message *
                                            </Label>
                                            <Textarea
                                                id="message"
                                                value={form.message}
                                                onChange={set("message")}
                                                rows={5}
                                                className={`mt-2 ${inputClass}`}
                                                placeholder="Briefly describe your project, current stage, and what you need reviewed or decided."
                                                data-testid="form-message-input"
                                            />
                                            {errText("message")}
                                        </div>
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-navy px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-amber disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                                        data-testid="form-submit-btn"
                                    >
                                        {submitting ? "Submitting..." : "Request Consultation"}
                                        {!submitting && <ArrowRight size={14} />}
                                    </button>
                                    <p className="mt-4 text-xs leading-relaxed text-slate-500">
                                        Your details are used only to respond to this enquiry. They are never published
                                        or shared.
                                    </p>
                                </form>
                            )}
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
